import { useEffect, useState } from "react";
import { toast } from "sonner";
import { API } from "@/App";
import {
  Building2,
  Loader2,
  ExternalLink,
  AlertTriangle,
  TrendingUp,
  Users,
  DollarSign,
  Clock,
  ChevronRight,
  X,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetClose } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const fmtMoney = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const relTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const days = Math.floor(ms / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

/**
 * NetworkDrilldownDrawer — slides in from the right when an admin clicks
 * the "View network" button on a parent-network hospital card.
 * Aggregates open backorders, YTD spend, and last contact across every
 * subsidiary clinic linked via parent_id.
 */
export default function NetworkDrilldownDrawer({ network, open, onOpenChange, token, onOpenHospital }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !network?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/admin/health-systems/${network.id}/network-stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.detail || "Failed to load network stats");
        }
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (err) {
        toast.error(err.message);
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, network?.id, token]);

  const totals = data?.totals || {};
  const subsidiaries = data?.subsidiaries || [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto p-0"
        data-testid="network-drilldown-drawer"
      >
        <div className="bg-gradient-to-br from-violet-600 to-indigo-700 text-white p-6 sticky top-0 z-10">
          <SheetClose asChild>
            <button
              type="button"
              className="absolute right-4 top-4 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/15 hover:bg-white/30 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white/60"
              aria-label="Close network drilldown"
              data-testid="network-drilldown-close"
            >
              <X className="w-4 h-4" />
            </button>
          </SheetClose>
          <SheetHeader className="text-left">
            <div className="flex items-center gap-2 text-violet-200 text-xs font-bold uppercase tracking-wider">
              <Building2 className="w-3.5 h-3.5" />
              Network Parent
            </div>
            <SheetTitle className="text-2xl text-white" data-testid="network-drawer-title">
              {network?.name || "Network"}
            </SheetTitle>
            <SheetDescription className="text-violet-100 text-sm">
              Aggregated rollup across all subsidiary hospitals &amp; clinics.
            </SheetDescription>
          </SheetHeader>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-7 h-7 animate-spin text-violet-600" />
          </div>
        ) : !data ? (
          <div className="p-6 text-center text-slate-500">
            <AlertTriangle className="w-10 h-10 mx-auto text-amber-500 mb-2" />
            <p className="text-sm">Could not load network statistics.</p>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* KPI tiles */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <KpiTile
                icon={<Building2 className="w-4 h-4" />}
                label="Subsidiaries"
                value={totals.subsidiary_count ?? 0}
                tone="violet"
                testId="kpi-subsidiaries"
              />
              <KpiTile
                icon={<AlertTriangle className="w-4 h-4" />}
                label="Open Backorders"
                value={totals.open_backorders ?? 0}
                tone={totals.open_backorders > 0 ? "rose" : "emerald"}
                testId="kpi-open-backorders"
              />
              <KpiTile
                icon={<DollarSign className="w-4 h-4" />}
                label="YTD Spend"
                value={fmtMoney(totals.ytd_spend)}
                tone="emerald"
                testId="kpi-ytd-spend"
              />
              <KpiTile
                icon={<TrendingUp className="w-4 h-4" />}
                label="Active (30d)"
                value={`${totals.active_subsidiaries_30d ?? 0}/${totals.subsidiary_count ?? 0}`}
                tone="blue"
                testId="kpi-active-30d"
              />
            </div>

            {/* Subsidiary table */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                <h4 className="font-semibold text-slate-900 text-sm flex items-center gap-2">
                  <Users className="w-4 h-4 text-violet-600" />
                  Subsidiary Hospitals
                  <Badge className="bg-violet-100 text-violet-700 text-[10px]">{subsidiaries.length}</Badge>
                </h4>
              </div>

              {subsidiaries.length === 0 ? (
                <div className="px-4 py-10 text-center text-slate-500 text-sm">
                  <Building2 className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  No hospitals are attached to this network yet.
                  <p className="text-xs mt-1 text-slate-400">
                    Set <strong>Network → attach a child</strong> on each subsidiary&apos;s card to roll them up here.
                  </p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50/60 text-xs text-slate-500 uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Subsidiary</th>
                      <th className="text-right px-2 py-2 font-medium">Open BO</th>
                      <th className="text-right px-2 py-2 font-medium">YTD Spend</th>
                      <th className="text-right px-4 py-2 font-medium">Last Contact</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {subsidiaries.map((s) => (
                      <tr
                        key={s.id}
                        className="hover:bg-violet-50/40 cursor-pointer"
                        onClick={() => onOpenHospital?.(s.id)}
                        data-testid={`subsidiary-row-${s.id}`}
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900 flex items-center gap-2">
                            {s.name}
                            <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
                          </div>
                          <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                            {s.city && s.state ? `${s.city}, ${s.state}` : "—"}
                            {s.assigned_rep_name && (
                              <span className="text-emerald-700 font-medium">· Rep: {s.assigned_rep_name}</span>
                            )}
                            {s.facilities_count > 0 && (
                              <span>· {s.facilities_count} facilities</span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-3 text-right">
                          {s.open_backorders > 0 ? (
                            <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-xs font-bold">
                              {s.open_backorders}
                            </span>
                          ) : (
                            <span className="text-slate-300 text-xs">0</span>
                          )}
                        </td>
                        <td className="px-2 py-3 text-right text-slate-900 font-medium tabular-nums">
                          {fmtMoney(s.ytd_spend)}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-500 text-xs">
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {relTime(s.last_contact)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="text-[11px] text-slate-400 text-right">
              Generated {data.generated_at ? new Date(data.generated_at).toLocaleString() : "—"}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function KpiTile({ icon, label, value, tone = "slate", testId }) {
  const tones = {
    violet: "border-violet-200 bg-violet-50 text-violet-700",
    rose: "border-rose-200 bg-rose-50 text-rose-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
    slate: "border-slate-200 bg-slate-50 text-slate-700",
  };
  return (
    <div className={`rounded-xl border ${tones[tone]} p-3`} data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold opacity-80">
        {icon}
        {label}
      </div>
      <div className="text-xl font-bold text-slate-900 mt-1 tabular-nums">{value}</div>
    </div>
  );
}
